import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { format } from 'date-fns';
import { useCategories, useRegions } from '../../lib/taxonomy';

// Strip HTML tags for preview
const getPlainText = (html) => {
  const tmp = document.createElement('DIV');
  tmp.innerHTML = html || '';
  return tmp.textContent || tmp.innerText || '';
};

const formatDate = (dateStr) => {
  try {
    return format(new Date(dateStr), 'MMM d, yyyy');
  } catch {
    return dateStr;
  }
};

/**
 * One story, laid out like a newspaper front page. Variants:
 *   default   photo on top, kicker, headline, summary, meta (grids)
 *   top       large photo, big headline, byline and summary (Top Stories lead)
 *   list      small photo on the left, headline and byline (Top Stories lists)
 *   lead      text beside a large photo (the day's top story)
 *   row       text column beside a wide photo (the front-page story stream)
 *   feature   photo on top with a large, lighter headline (right rail lead)
 *   compact   headline and summary with a small photo on the right (lists)
 *   tile      photo and headline only (story-page recirculation)
 *   headline  text only (secondary lists)
 */
export const NewsCard = ({ article, variant = 'default', showSummary = true }) => {
  const { t, isHindi } = useLanguage();
  const { label } = useCategories();
  const { districtLabel } = useRegions();

  const title = isHindi && article.title_hi ? article.title_hi : article.title;
  const categoryName = label(article.category, isHindi);
  const placeName = districtLabel(article.district, isHindi);
  const content = isHindi && article.content_hi ? article.content_hi : article.content;
  const plainContent = getPlainText(content);
  const minutes = Math.max(1, Math.round(plainContent.split(/\s+/).filter(Boolean).length / 200));

  const headFont = isHindi ? 'font-hindi-heading' : 'font-heading';
  const bodyFont = isHindi ? 'font-hindi' : '';
  const to = `/article/${article.article_id}`;

  const kicker = (
    <p className={`kicker mb-1.5 ${bodyFont}`}>
      {categoryName}
      {placeName && <span className="font-normal text-[var(--ink-muted)]"> · {placeName}</span>}
      {article.is_featured && variant !== 'headline' && (
        <span className="featured-badge ml-2 align-middle" data-testid="featured-badge">{t('featured')}</span>
      )}
    </p>
  );

  const meta = (
    <p className="story-meta mt-2">
      {isHindi ? `${minutes} मिनट` : `${minutes} min read`}
      <span className="mx-1.5">·</span>
      {formatDate(article.created_at)}
    </p>
  );

  const photo = (className, credit = true) => article.image_url && (
    <figure className={className}>
      <div className="aspect-[3/2] overflow-hidden bg-[var(--bg-subtle)]">
        <img
          src={article.image_url}
          alt={title}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover"
          onError={(e) => { e.currentTarget.closest('figure').style.display = 'none'; }}
        />
      </div>
      {credit && article.source_name && <figcaption className="photo-credit">{article.source_name}</figcaption>}
    </figure>
  );

  if (variant === 'lead') {
    return (
      <Link
        to={to}
        className="group grid md:grid-cols-5 gap-4 md:gap-6"
        data-testid={`news-card-hero-${article.article_id}`}
      >
        <div className={`order-2 md:order-1 ${article.image_url ? 'md:col-span-2' : 'md:col-span-5'}`}>
          {kicker}
          <h2 className={`news-headline text-[1.625rem] md:text-[1.875rem] font-bold leading-[1.15] text-[var(--ink)] ${headFont}`}>
            {title}
          </h2>
          {showSummary && (
            <p className={`story-summary mt-3 line-clamp-4 ${bodyFont}`}>{plainContent}</p>
          )}
          {meta}
        </div>
        {photo('order-1 md:order-2 md:col-span-3')}
      </Link>
    );
  }

  // Author and date, for the Top Stories section
  const byline = (
    <p className="mt-2 font-ui text-[13px] text-[var(--ink-muted)]">
      <span className={`text-[var(--ink-soft)] ${bodyFont}`}>{article.author_name}</span>
      <span className="mx-1.5">·</span>
      {formatDate(article.created_at)}
    </p>
  );

  if (variant === 'top') {
    return (
      <Link to={to} className="group block" data-testid={`news-card-hero-${article.article_id}`}>
        {photo('mb-4', false)}
        <h2 className={`news-headline text-[1.75rem] sm:text-[2rem] font-bold leading-[1.15] text-[var(--ink)] ${headFont}`}>
          {title}
        </h2>
        {byline}
        {showSummary && (
          <p className={`story-summary mt-3 text-base line-clamp-3 ${bodyFont}`}>{plainContent}</p>
        )}
      </Link>
    );
  }

  if (variant === 'list') {
    return (
      <Link to={to} className="group flex gap-4 items-start" data-testid={`news-card-sub-${article.article_id}`}>
        {photo('w-28 sm:w-40 shrink-0', false)}
        <div className="flex-1 min-w-0">
          <h3 className={`news-headline text-[1.0625rem] sm:text-lg font-bold leading-snug text-[var(--ink)] line-clamp-4 ${headFont}`}>
            {title}
          </h3>
          {byline}
        </div>
      </Link>
    );
  }

  if (variant === 'row') {
    return (
      <Link to={to} className="group grid md:grid-cols-12 gap-4 md:gap-6" data-testid={`news-card-${article.article_id}`}>
        <div className={`order-2 md:order-1 ${article.image_url ? 'md:col-span-5' : 'md:col-span-12'}`}>
          {kicker}
          <h3 className={`news-headline text-[1.375rem] font-bold leading-tight text-[var(--ink)] ${headFont}`}>
            {title}
          </h3>
          {showSummary && (
            <p className={`story-summary mt-2 line-clamp-4 ${bodyFont}`}>{plainContent}</p>
          )}
          {meta}
        </div>
        {photo('order-1 md:order-2 md:col-span-7')}
      </Link>
    );
  }

  if (variant === 'feature') {
    return (
      <Link to={to} className="group block" data-testid={`news-card-${article.article_id}`}>
        {photo('mb-3')}
        {kicker}
        <h3 className={`news-headline text-[1.75rem] font-normal leading-[1.15] text-[var(--ink)] ${headFont}`}>
          {title}
        </h3>
        {showSummary && (
          <p className={`story-summary mt-2 line-clamp-3 ${bodyFont}`}>{plainContent}</p>
        )}
        {meta}
      </Link>
    );
  }

  if (variant === 'compact') {
    return (
      <Link to={to} className="group flex gap-4" data-testid={`news-card-sub-${article.article_id}`}>
        <div className="flex-1 min-w-0">
          {kicker}
          <h3 className={`news-headline text-[1.0625rem] font-bold leading-snug text-[var(--ink)] ${headFont}`}>
            {title}
          </h3>
          {showSummary && (
            <p className={`story-summary mt-1.5 text-sm line-clamp-2 ${bodyFont}`}>{plainContent}</p>
          )}
          {meta}
        </div>
        {photo('w-28 sm:w-36 shrink-0', false)}
      </Link>
    );
  }

  if (variant === 'tile') {
    return (
      <Link to={to} className="group block" data-testid={`news-card-${article.article_id}`}>
        {photo('mb-3', false)}
        <h3 className={`news-headline text-[1.1875rem] font-medium leading-snug text-[var(--ink)] ${headFont}`}>
          {title}
        </h3>
      </Link>
    );
  }

  if (variant === 'headline') {
    return (
      <Link to={to} className="group block" data-testid={`news-card-${article.article_id}`}>
        <h3 className={`news-headline text-base font-bold leading-snug text-[var(--ink)] ${headFont}`}>
          {title}
        </h3>
        {meta}
      </Link>
    );
  }

  // Default: photo on top
  return (
    <Link to={to} className="group block" data-testid={`news-card-${article.article_id}`}>
      {photo('mb-3')}
      {kicker}
      <h3 className={`news-headline text-xl font-bold leading-tight text-[var(--ink)] ${headFont}`}>
        {title}
      </h3>
      {showSummary && (
        <p className={`story-summary mt-2 line-clamp-3 ${bodyFont}`}>{plainContent}</p>
      )}
      {meta}
    </Link>
  );
};

export const NewsCardSkeleton = ({ variant = 'default' }) => {
  if (variant === 'compact') {
    return (
      <div className="flex gap-4 animate-pulse">
        <div className="flex-1 space-y-2">
          <div className="h-3 bg-gray-200 w-16" />
          <div className="h-5 bg-gray-200" />
          <div className="h-5 bg-gray-200 w-2/3" />
        </div>
        <div className="w-28 sm:w-36 aspect-[3/2] bg-gray-200 shrink-0" />
      </div>
    );
  }
  return (
    <div className="animate-pulse">
      <div className="aspect-[3/2] bg-gray-200 mb-3" />
      <div className="h-3 bg-gray-200 w-20 mb-2" />
      <div className="h-5 bg-gray-200 mb-2" />
      <div className="h-5 bg-gray-200 w-3/4 mb-3" />
      <div className="h-3 bg-gray-200 w-24" />
    </div>
  );
};
